// components/ui/RadioRow.tsx
// Bordered radio option. Selected state uses accent border + soft tint.
// Replaces the old card-style radio pattern in BasicsStep/ComplexityStep.

import React from 'react';

interface RadioRowProps {
    label: string;
    value: string;
    checked: boolean;
    onChange: (value: string) => void;
    description?: string;
    name?: string;
    inline?: boolean;
}

export function RadioRow({ label, value, checked, onChange, description, name, inline = false }: RadioRowProps) {
    return (
        <label
            className={`
                ${checked ? 'radio-selected' : ''}
                flex items-start gap-3 cursor-pointer transition-colors
                px-4 py-3.5 mb-2
                border
                ${checked
                    ? 'border-[#3730A3] bg-[#EEF0FA]'
                    : 'border-[#E8E4DB] bg-white hover:border-[#AFA99F]'}
                ${inline ? 'flex-none' : ''}
            `}
        >
            <input
                type="radio"
                name={name}
                value={value}
                checked={checked}
                onChange={() => onChange(value)}
                className="sr-only"
            />
            <span
                className={`
                    mt-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0
                    border-[1.5px]
                    ${checked ? 'border-[#3730A3]' : 'border-[#AFA99F]'}
                `}
            >
                {checked && <span className="w-1.5 h-1.5 rounded-full bg-[#3730A3]" />}
            </span>
            <span className="flex-1">
                <span className="block text-[14px] text-[#1A1A1A]">{label}</span>
                {description && (
                    <span className="block text-[12px] text-[#6B6762] mt-1 leading-relaxed">
                        {description}
                    </span>
                )}
            </span>
        </label>
    );
}

interface RadioGroupProps {
    name: string;
    value: string;
    onChange: (value: string) => void;
    options: Array<{ value: string; label: string; description?: string }>;
    inline?: boolean;
}

export function RadioGroup({ name, value, onChange, options, inline = false }: RadioGroupProps) {
    return (
        <div className={inline ? 'flex flex-wrap gap-2' : ''}>
            {options.map((opt) => (
                <RadioRow
                    key={opt.value}
                    name={name}
                    value={opt.value}
                    label={opt.label}
                    description={opt.description}
                    checked={value === opt.value}
                    onChange={onChange}
                    inline={inline}
                />
            ))}
        </div>
    );
}
